import React, { useEffect, useState, useRef } from "react";
import { api, fmtRelative } from "../lib/api";
import { Bell, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useWS } from "../lib/ws";
import { playNotificationVariant, notifVariantFor, isSoundOn, setSoundOn as persistSoundOn } from "../lib/sound";
import { useAuth } from "../lib/auth";
import { pushSupported, pushPermission, enablePush, ensurePushIfGranted } from "../lib/push";

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [soundOn, setSoundOn] = useState(isSoundOn());
  const [pushState, setPushState] = useState(() => (pushSupported() ? pushPermission() : "unsupported"));
  const prevUnreadRef = useRef(0);

  const enableBgAlerts = async () => {
    try {
      await enablePush();
      setPushState("granted");
    } catch (e) {
      const code = e?.message;
      if (code === "denied") {
        setPushState("denied");
        alert("Notifications are blocked. Enable them for this site in your browser/phone settings, then try again.");
      } else if (code === "not-supported") {
        alert("On iPhone, first tap the Share button → 'Add to Home Screen', open the app from your home screen, then enable alerts. On desktop, use Chrome, Edge or Firefox.");
      } else {
        alert("Couldn't turn on background alerts. Please try again.");
      }
    }
  };
  const firstLoadRef = useRef(true);
  const { subscribe } = useWS();

  // Each user gets their own sound: their saved choice, else one derived from their id.
  const myVariant = () => (typeof user?.notif_sound === "number" ? user.notif_sound : notifVariantFor(user?.id));
  const playMine = () => playNotificationVariant(myVariant());

  const toggleSound = () => {
    setSoundOn(s => {
      const next = !s;
      persistSoundOn(next);
      return next;
    });
  };

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      const newList = r.data || [];
      const unread = newList.filter(n => !n.read).length;
      // Don't beep on the very first load (pre-existing unread shouldn't ring).
      if (!firstLoadRef.current && unread > prevUnreadRef.current) {
        playMine();
      }
      firstLoadRef.current = false;
      prevUnreadRef.current = unread;
      setItems(newList);
    } catch {}
  };

  useEffect(() => {
    load();
    ensurePushIfGranted(); // keep subscription fresh for users who already allowed alerts
    // WebSocket push for instant notifications
    const unsub = subscribe("notification", (n) => {
      setItems(prev => [n, ...prev]);
      prevUnreadRef.current += 1;
      playMine();
    });
    // Lightweight poll as fallback (60s)
    const id = setInterval(load, 60000);
    return () => { clearInterval(id); unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);

  const unread = items.filter(n => !n.read).length;
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const markOne = async (id) => { await api.patch(`/notifications/${id}/read`); load(); };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-lg hover:bg-gray-100" aria-label="Notifications">
          <Bell size={18} strokeWidth={1.8} className="text-gray-700"/>
          {unread > 0 && (
            <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 bg-[#4f87ff] text-white text-[10px] font-semibold h-4 min-w-4 px-1 rounded-full flex items-center justify-center">{unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="font-medium" style={{fontFamily:'Outfit'}}>Notifications</div>
          <div className="flex items-center gap-2">
            <button data-testid="sound-toggle" onClick={toggleSound} title="Notification & chat sounds" className="text-[11px] text-gray-500 hover:text-gray-900">{soundOn ? "🔔 On" : "🔕 Off"}</button>
            {unread > 0 && <button data-testid="mark-all-read" onClick={markAll} className="text-[11px] text-[#4f87ff] hover:underline">Mark all read</button>}
          </div>
        </div>
        {pushState !== "granted" && pushState !== "unsupported" && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-[#eef4ff] border-b">
            <span className="text-[11px] text-gray-600">Get alerts even when the app is closed</span>
            <button data-testid="enable-push" onClick={enableBgAlerts} className="text-[11px] font-medium text-white bg-[#4f87ff] hover:bg-[#3f73e6] rounded-full px-2.5 py-1">Enable</button>
          </div>
        )}
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <div className="p-6 text-center text-sm text-gray-400">You're all caught up</div>}
          {items.map(n => (
            <div key={n.id} data-testid={`notif-${n.id}`} className={`p-3 border-b flex gap-2 items-start ${!n.read ? "bg-[#FFF8F2]" : ""}`}>
              <div className={`h-2 w-2 rounded-full mt-1.5 ${!n.read ? "bg-[#4f87ff]" : "bg-gray-300"}`}/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{n.title}</div>
                <div className="text-xs text-gray-600 line-clamp-2">{n.body}</div>
                <div className="text-[10px] text-gray-400 mt-1">{fmtRelative(n.created_at)}</div>
              </div>
              {!n.read && <button onClick={()=>markOne(n.id)} className="text-gray-400 hover:text-[#4f87ff]"><Check size={14}/></button>}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
